import { useEffect, useRef, useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Pencil, Check, X } from 'lucide-react';

/**
 * Click-to-edit text.
 * Props:
 *  - field: property name to persist (e.g., "hero_title_main")
 *  - target: "settings" | "page:home" | "page:about" ... (which endpoint to save to)
 *  - as: element type to render as span/h1/h2/p/div (defaults to span)
 *  - multiline: allow \n
 *  - children: current text to display (parent computes with fallback)
 */
export default function EditText({ field, target = 'settings', as: Tag = 'span', multiline = false, children, className = '', ...rest }) {
  const { editMode } = useEditMode();
  const { refresh, loadPageContent } = useSiteData();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select?.(); } }, [editing]);

  const start = (e) => {
    if (!editMode) return;
    e.preventDefault(); e.stopPropagation();
    const current = typeof children === 'string' ? children :
                    Array.isArray(children) ? children.filter(c => typeof c === 'string').join('') : '';
    setVal(current);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (target === 'settings') {
        await api.patch('/site/settings', { [field]: val });
        refresh(['settings']);
      } else if (target?.startsWith('page:')) {
        const slug = target.slice(5);
        await api.patch(`/site/page-content/${slug}`, { [field]: val });
        loadPageContent(slug);
      }
      toast.success('Saved');
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const cancel = () => setEditing(false);

  if (!editMode) return <Tag className={className} {...rest}>{children}</Tag>;

  if (editing) {
    return (
      <span className="inline-block relative align-baseline" style={{ zIndex: 60 }}>
        {multiline ? (
          <textarea
            ref={ref} value={val} onChange={(e) => setVal(e.target.value)}
            rows={Math.min(8, Math.max(2, val.split('\n').length + 1))}
            className="w-full min-w-[280px] px-3 py-2 border-2 border-gold bg-white rounded shadow-lg text-charcoal text-base"
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }}
          />
        ) : (
          <input
            ref={ref} value={val} onChange={(e) => setVal(e.target.value)}
            className="min-w-[240px] px-3 py-1 border-2 border-gold bg-white rounded shadow-lg text-charcoal font-sans text-base"
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); if (e.key === 'Enter') save(); }}
          />
        )}
        <span className="inline-flex items-center gap-1 ml-2 align-middle">
          <button onClick={save} disabled={busy} title="Save" className="p-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
          <button onClick={cancel} title="Cancel" className="p-1 rounded bg-gray-500 text-white hover:bg-gray-600"><X className="w-3.5 h-3.5" /></button>
        </span>
      </span>
    );
  }

  return (
    <Tag
      onClick={start}
      className={`${className} editable-text`}
      title="Click to edit"
      data-testid={`edit-${field}`}
      {...rest}
    >
      {children}
      <Pencil className="edit-pencil w-3 h-3" />
    </Tag>
  );
}
